<template>
  <v-dialog
    v-model="isOpen"
    width="92%"
    max-width="1200"
    persistent
    class="doc-preview-overlay"
    :z-index="10050"
  >
    <v-card class="document-preview-modal">
      <v-card-title class="document-preview-header">
        <div class="header-index">[{{ document?.index || 1 }}]</div>
        <span class="header-title">{{ document?.file_name || 'Döküman' }}</span>
        <v-spacer />
        <v-btn icon variant="text" @click="close" size="small">
          <span class="material-symbols-outlined">close</span>
        </v-btn>
      </v-card-title>

      <v-card-text class="document-preview-body">
        <!-- Alıntı Bölümü -->
        <div class="quote-section" v-if="document?.quote">
          <h4>
            <span class="material-symbols-outlined">format_quote</span>
            İlgili Alıntı
          </h4>
          <div class="quote-content">
            {{ document.quote }}
          </div>
        </div>

        <!-- Alıntı yoksa bilgi mesajı (içerik yüklenirken/yüklendiğinde gizle) -->
        <div v-else-if="!loadingContent && !fullContent && !contentUnavailable" class="no-quote-section">
          <v-alert type="info" variant="tonal" density="compact">
            <div class="d-flex align-center gap-2">
              <span class="material-symbols-outlined">info</span>
              <span>Bu kaynak için alıntı metni mevcut değil.</span>
            </div>
          </v-alert>
        </div>

        <!-- Tam İçerik Bölümü -->
        <div v-if="fullContent" class="full-content-section">
          <h4>
            <span class="material-symbols-outlined">article</span>
            Tam İçerik
          </h4>
          <div class="full-content" v-html="fullContentHtml"></div>
        </div>

        <!-- Tam İçerik Yükleme Butonu -->
        <div v-else-if="!loadingContent && !contentUnavailable" class="load-content-section">
          <v-btn
            variant="outlined"
            color="primary"
            @click="loadFullContent"
            :disabled="!document?.file_name"
          >
            <span class="material-symbols-outlined">expand_more</span>
            Tamamını Göster
          </v-btn>
          <p v-if="!document?.file_name" class="text-caption text-grey mt-2">
            Dosya adı bulunamadı
          </p>
        </div>

        <!-- İçerik mevcut değil -->
        <div v-if="contentUnavailable" class="content-unavailable-section">
          <v-alert type="warning" variant="tonal">
            <div class="d-flex align-center gap-2">
              <span class="material-symbols-outlined">warning</span>
              <div>
                <strong>Önizleme yüklenemedi</strong>
                <p class="text-caption mb-0 mt-1">Bu dosyanın içeriği görüntülenemedi. Dosyayı indirerek açabilirsiniz. Dosya: {{ document?.file_name }}</p>
              </div>
            </div>
          </v-alert>
        </div>

        <!-- Yükleniyor -->
        <div v-if="loadingContent" class="loading-section">
          <v-progress-circular indeterminate color="primary" size="24" />
          <span>İçerik yükleniyor...</span>
        </div>

        <!-- Hata -->
        <v-alert v-if="error" type="error" variant="tonal" class="mt-4" closable @click:close="error = ''">
          {{ error }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="document-preview-footer">
        <v-btn variant="text" @click="close">Kapat</v-btn>
        <v-spacer />
        <v-btn variant="tonal" color="primary" @click="addToChat">
          <span class="material-symbols-outlined">add_comment</span>
          Sohbete Ekle
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from "vue";
import axios from "axios";

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

interface DocumentData {
  file_id?: string;
  file_name: string;
  quote?: string;
  index?: number;
  // Doktrin kitapları (folder === 'kitap'): sayfa aralıklı önizleme için
  folder?: string;
  sayfa_no?: string;
  eser_adi?: string;
}

export default defineComponent({
  name: "DocumentPreviewModal",

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    document: {
      type: Object as PropType<DocumentData | null>,
      default: null,
    },
  },

  emits: ["update:modelValue", "add-to-chat"],

  setup(props, { emit }) {
    const loadingContent = ref(false);
    const fullContent = ref("");
    const fullContentHtml = ref("");
    const error = ref("");
    const contentUnavailable = ref(false);

    const isOpen = computed({
      get: () => props.modelValue,
      set: (val) => emit("update:modelValue", val),
    });

    const authHeaders = () => {
      const token = localStorage.getItem("auth_token");
      return {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      };
    };

    const loadFullContent = async () => {
      if (!props.document?.file_name) {
        error.value = "Dosya adı bulunamadı.";
        return;
      }

      loadingContent.value = true;
      error.value = "";
      contentUnavailable.value = false;

      try {
        console.log('[DocumentPreview] Loading file:', props.document.file_name);

        const fileId = props.document.file_id || 'local';
        // Doktrin kitapları binlerce sayfa: tüm belgeyi değil, kaynak kartındaki
        // sayfa aralığını iste (backend parser-api/PyMuPDF yoluna sapar).
        const params: Record<string, string> = { filename: props.document.file_name };
        if (props.document.folder === 'kitap' && props.document.sayfa_no) {
          params.pages = String(props.document.sayfa_no);
        }
        const res = await axios.get(
          `${API_BASE_URL}/files/${fileId}/content`,
          { headers: authHeaders(), params }
        );

        console.log('[DocumentPreview] Response:', res.data);

        if (res.data.ok) {
          fullContent.value = res.data.content || "";
          fullContentHtml.value = res.data.content_html || res.data.content || "";

          if (!fullContent.value) {
            contentUnavailable.value = true;
          }
        } else {
          // API'den hata geldi - muhtemelen dosya erişilemez
          contentUnavailable.value = true;
        }
      } catch (err: any) {
        console.error('[DocumentPreview] Error:', err);
        // Vector Store dosyaları doğrudan indirilemez
        contentUnavailable.value = true;
      } finally {
        loadingContent.value = false;
      }
    };

    const addToChat = () => {
      const text = fullContent.value
        ? `[Kaynak: ${props.document?.file_name}]\n\n${fullContent.value}`
        : `[Kaynak: ${props.document?.file_name}]\n\n${props.document?.quote || ""}`;
      emit("add-to-chat", text);
      close();
    };

    const close = () => {
      isOpen.value = false;
    };

    const reset = () => {
      loadingContent.value = false;
      fullContent.value = "";
      fullContentHtml.value = "";
      error.value = "";
      contentUnavailable.value = false;
    };

    // Modal açıldığında reset + içeriği otomatik yükle (dosya adı varsa).
    // Kaynak öğelerinde quote/file_id olmayabilir; loadFullContent yalnız file_name
    // ile de çalışır (file_id yoksa 'local' varsayılır).
    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal) {
          reset();
          if (props.document?.file_name) {
            loadFullContent();
          }
        }
      }
    );

    return {
      isOpen,
      loadingContent,
      fullContent,
      fullContentHtml,
      error,
      contentUnavailable,
      loadFullContent,
      addToChat,
      close,
    };
  },
});
</script>

<style lang="scss" scoped>
.document-preview-modal {
  border-radius: 16px !important;
  overflow: hidden;
}

.document-preview-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #5a6270 0%, #6b7280 100%);
  color: white;

  .header-index {
    background: rgba(255, 255, 255, 0.2);
    padding: 4px 10px;
    border-radius: 6px;
    font-weight: 700;
    font-size: 14px;
  }

  .header-title {
    font-size: 16px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.document-preview-body {
  padding: 24px !important;
  max-height: 65vh;
  overflow-y: auto;
}

.quote-section,
.full-content-section {
  margin-bottom: 24px;

  h4 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e0e0e0;

    .material-symbols-outlined {
      font-size: 20px;
      color: #5a6270;
    }
  }
}

.quote-content {
  background: #fff8e1;
  border-left: 4px solid #ffc107;
  padding: 16px 20px;
  border-radius: 0 12px 12px 0;
  font-size: 14px;
  line-height: 1.7;
  color: #444;
  font-style: italic;
}

.full-content {
  background: #f8faf9;
  border-left: 4px solid #5a6270;
  padding: 16px 20px;
  border-radius: 0 12px 12px 0;
  font-size: 14px;
  line-height: 1.7;
  color: #333;
  max-height: 400px;
  overflow-y: auto;

  :deep(p) {
    margin-bottom: 12px;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3) {
    margin-top: 16px;
    margin-bottom: 8px;
    color: #5a6270;
  }
}

.load-content-section {
  display: flex;
  justify-content: center;
  padding: 16px;

  .v-btn {
    .material-symbols-outlined {
      margin-right: 8px;
    }
  }
}

.loading-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: #666;
}

.no-quote-section {
  margin-bottom: 24px;

  .material-symbols-outlined {
    font-size: 20px;
  }
}

.content-unavailable-section {
  margin-top: 16px;

  .material-symbols-outlined {
    font-size: 20px;
    color: #f57c00;
  }
}

.document-preview-footer {
  padding: 16px 24px;
  border-top: 1px solid #eee;

  .v-btn {
    .material-symbols-outlined {
      font-size: 18px;
      margin-right: 6px;
    }
  }
}
</style>

<!-- Global (scoped değil): overlay body'ye teleport edildiği için scoped erişemez.
     Önizleme açıkken arka planı bulanıklaştır + karart. -->
<style>
.doc-preview-overlay .v-overlay__scrim,
.v-overlay:has(.document-preview-modal) .v-overlay__scrim {
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  background: rgba(28, 37, 32, 0.45);
  opacity: 1;
}
</style>
